/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ry-bento — 2D cell grid (the bento box)                                  ║
 * ║                                                                            ║
 * ║  Sibling of ry-n-split, NOT a nested splitter. n-split is a 1-D split     ║
 * ║  tree (hierarchical tilings only); bento is a FLAT list of percent rects  ║
 * ║  that tile the container. Edges are DERIVED from rect adjacency, so it    ║
 * ║  expresses non-hierarchical layouts (the pinwheel) no split tree can,     ║
 * ║  and cells can be split/removed at RUNTIME in either axis.                ║
 * ║                                                                            ║
 * ║  Ported, not invented:                                                     ║
 * ║    • blitzen2d bento-engines "absolute" variation (Ryan's project) — the  ║
 * ║      cells-as-percent-rects model, edge derivation + rail-merge cascade   ║
 * ║      rule, slide clamp math, tiling validation, split/remove-with-absorb. ║
 * ║    • Its gutter layer (workspace/gutters.js) — overlay strips, baseline-  ║
 * ║      at-drag-start preview, snap-to-edge/bounds/tug-home, actualDelta     ║
 * ║      strip positioning.                                                    ║
 * ║  Added here: keyboard + ARIA on gutters (ported from ry-n-split).         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

/**
 * @component ry-bento
 * @category  layout
 * @summary   2D bento-box grid (ports blitzen2d's bento engine). Direct children ARE the cells — each is a percent rect (`cell="x y w h"`, 0–100) and together they must tile the container exactly; omit the attrs and children auto-tile as equal columns. `resizable` derives draggable gutters from rect adjacency: aligned boundaries ("rails") drag independently, collinear ones cascade together — pinwheel layouts work. Cells split (`split()`) and remove (`remove()`, neighbors absorb) at runtime in either axis. Percent storage makes layouts responsive; `persist="id"` remembers them.
 *
 * @attribute resizable  boolean   default=false   Derive drag gutters from cell adjacency; enable pointer/keyboard resize.
 * @attribute editable   boolean   default=false   Mount blitzen2d-style edit chrome on every cell: hovering near an edge slides in a `+` that splits the cell on that axis (the new empty cell takes the freed half); a `✕` in the top-right removes the cell (disabled, with a title explaining why, when no neighbor set cleanly absorbs its space); a `⠿` handle in the top-left drags the cell — drop it on another cell to swap their rects (content travels). LIVE: toggle it (and `resizable`) at runtime — `el.toggleAttribute('editable')` is a lock/edit switch; chrome and gutters mount/unmount on the spot.
 * @attribute lockable   boolean   default=false   Render the bento's own lock/edit toggle — a small button pinned to the bottom-right corner that flips `editable` (✎ = locked, click to edit; ✓ = editing, click to lock). Locked means LOCKED: `resizable` gutters are suspended too until unlocked. The declarative way to get the mode switch without writing any JS.
 * @attribute gap        'none' | 'sm' | 'md' | 'lg'  default=(0.5rem)   Visual gutter between cells (transparent border inset — rects still touch exactly).
 * @attribute persist    string?                   localStorage key — saves/restores the percent-rect layout.
 * @attribute snap       number    default=4       Snap-align radius in px on EACH side (8px total zone by default) — a dragged edge this close to another edge, a container bound, or its start position pulls to EXACT alignment. Aligning across a boundary creates a rail, making the segments independently draggable again. Keep it small: it's also a dead zone around the grab point.
 * @attribute min        number    default=50      Minimum cell size in px along the dragged axis (a 5% floor also always applies).
 *
 * @attribute (per child) cell  string?   Percent rect `"x y w h"` (space or comma separated, each 0–100). All children need one and they must tile 100×100 exactly, or the whole set falls back to equal columns.
 *
 * @property  layout  {x,y,w,h}[]   Read-only — current cell rects in percent, child order.
 * @property  static animator  ((host, mutate) => void) | null   Global animation seat for structural changes (split/remove/swap). `null` (default) = instant. Set `RyBento.animator = bentoFlip` (the exported FLIP port) or any function that measures, calls `mutate()`, and plays. One seat app-wide, by design.
 *
 * @method splitCell(cell, axis?, side?)  Split a cell element in half along `axis` ('col' default, or 'row'); the fresh cell (same tag, empty) goes on `side` ('after' default, or 'before'). Returns the new element, or null if the cell is too small.
 * @method removeCell(cell)  Remove a cell element; neighbors on one side absorb its space. Returns false (and leaves the DOM alone) when no clean absorber exists.
 * @method canRemoveCell(cell)  True if removeCell(cell) would succeed.
 * @method swapCells(a, b)  Exchange two cells' rects — each element (content and all) takes the other's geometry. Returns false if either element isn't a cell.
 *
 * @event ry:resize
 *        detail: { layout: {x,y,w,h}[] }
 *        fires:  during gutter drag (per frame) + on commit.
 *
 * @event ry:beforesplit
 *        detail: { cell: HTMLElement, axis: 'col' | 'row', side: 'before' | 'after' }
 *        fires:  at the top of splitCell() — the `+` chrome or `el.splitCell()` — after the geometry check passed and before the new cell is created or inserted. `cell` is the cell ABOUT to be split (unlike `ry:split`, whose `cell` is the fresh one).
 *        cancelable: preventDefault() leaves the grid as-is; splitCell() returns null and `ry:split` does not fire
 *
 * @event ry:split
 *        detail: { cell: HTMLElement }
 *        fires:  after split() inserts the new cell.
 *
 * @event ry:beforeremove
 *        detail: { cell: HTMLElement, layout: {x,y,w,h}[] }
 *        fires:  at the top of removeCell() — the `✕` chrome or `el.removeCell()` — after an absorber was found and before the cell leaves the DOM. `cell` is the cell about to go; `layout` is the layout that would result.
 *        cancelable: preventDefault() keeps the cell; removeCell() returns false and `ry:remove` does not fire
 *
 * @event ry:remove
 *        detail: { layout: {x,y,w,h}[] }
 *        fires:  after remove() succeeds.
 *
 * @event ry:beforeswap
 *        detail: { a: HTMLElement, b: HTMLElement }
 *        fires:  at the top of swapCells() — a drag-handle drop or `el.swapCells()` — before either rect changes
 *        cancelable: preventDefault() leaves both cells where they are; swapCells() returns false and `ry:swap` does not fire
 *
 * @event ry:swap
 *        detail: { a: HTMLElement, b: HTMLElement }
 *        fires:  after swapCells() (or a drag-handle drop) exchanges two cells.
 *
 * @keyboard  Focused gutter: Arrow keys move that edge (Left/Right on vertical gutters, Up/Down on horizontal). Shift = 50px step, else 10px.
 *
 * @state     `data-ry-resizing` on the host during an active drag.
 * @state     `data-ry-empty` on any cell with no content besides the edit chrome (kept live via MutationObserver — filling the cell later clears it). Theme renders these as dashed "empty" slots under `[editable]`.
 *
 * @content   N direct children = N cells. Gutters are overlay strips inserted by JS when `[resizable]`. Cells are invisible positioning boxes — put a `ry-card` (or anything) inside and size it 100%.
 *
 * @gotcha  This is NOT a splitter tree. There is no orientation attribute — every layout is one flat set of rects, and a single bento expresses cross-axis arrangements (splits both ways, pinwheels) directly.
 *
 * @gotcha  Rects must tile exactly (no overlap, full coverage). Invalid `cell` attrs are rejected as a set with a console.warn and the children auto-tile as equal columns instead.
 *
 * @gotcha  Whether an edge drags alone or cascades is derived, not configured: perpendicular boundaries aligned exactly across an edge's end ("rails") make it independent; otherwise collinear segments move together. To restore independence, drag the misaligned perpendicular edge back into the snap zone — it pulls to exact alignment and the rail comes back.
 *
 * @gotcha  `persist` stores percent rects keyed by cell count — responsive across container sizes, but a saved layout is ignored if the child count differs (e.g. you split/removed at runtime and reloaded).
 *
 * @gotcha  Add/remove cells through `splitCell()`/`removeCell()` (or the `[editable]` chrome), not raw DOM insertion — a new child needs a rect carved out of its neighbors, which only the engine can do. (`removeCell`, not `remove` — the native `Element.remove()` is untouched.)
 *
 * @gotcha  Cells created by `+` / `splitCell()` are seeded to look like their sibling: if the split cell wraps a single element (e.g. a `ry-card`), the fresh cell gets an empty shallow clone of it (same tag/class/style, blank inside). No single wrapper → the fresh cell arrives empty and shows a dashed `data-ry-empty` slot until filled. Either way, listen for `ry:split` and fill `detail.cell` — the event fires before the edit chrome mounts, so replacing content wholesale (innerHTML/textContent) is safe there. With `[editable]`, the outer ~18px of each cell doubles as the split hover zone, above the cell's content.
 *
 * @gotcha  Animation hook: structural changes (split/remove/swap) are instant by default. Set `RyBento.animator = bentoFlip` (exported FLIP port from blitzen2d) — or any `(host, mutate) => void` that measures, calls `mutate()`, and plays — to make cells glide. One global seat, by design: layout motion should feel identical app-wide.
 *
 * @example auto-tile-columns
 * ```html
 * <ry-bento style="height:160px">
 *   <div><ry-card style="height:100%">A</ry-card></div>
 *   <div><ry-card style="height:100%">B</ry-card></div>
 *   <div><ry-card style="height:100%">C</ry-card></div>
 * </ry-bento>
 * ```
 *
 * @example pinwheel
 * ```html
 * <ry-bento resizable style="height:320px">
 *   <div cell="0 0 33.33 75"><ry-card style="height:100%">tall left</ry-card></div>
 *   <div cell="33.33 0 66.67 30"><ry-card style="height:100%">wide top</ry-card></div>
 *   <div cell="33.33 30 33.34 45"><ry-card style="height:100%">middle</ry-card></div>
 *   <div cell="66.67 30 33.33 70"><ry-card style="height:100%">tall right</ry-card></div>
 *   <div cell="0 75 66.67 25"><ry-card style="height:100%">wide bottom</ry-card></div>
 * </ry-bento>
 * ```
 *
 * @example dashboard-2x2
 * ```html
 * <ry-bento resizable persist="dash" style="height:320px">
 *   <div cell="0 0 50 50"><ry-card style="height:100%">Revenue</ry-card></div>
 *   <div cell="50 0 50 50"><ry-card style="height:100%">Users</ry-card></div>
 *   <div cell="0 50 50 50"><ry-card style="height:100%">Orders</ry-card></div>
 *   <div cell="50 50 50 50"><ry-card style="height:100%">Errors</ry-card></div>
 * </ry-bento>
 * ```
 *
 * @example editable-workspace
 * ```html
 * <ry-bento resizable editable lockable style="height:320px">
 *   <div cell="0 0 50 100"><ry-card style="height:100%">Hover an edge → + splits. Corner ✕ removes. ⠿ drags to swap.</ry-card></div>
 *   <div cell="50 0 50 100"><ry-card style="height:100%">Bottom-right button locks/unlocks editing.</ry-card></div>
 * </ry-bento>
 * ```
 *
 * @tutorial auto-tile-equal-columns
 * ```js
 * const el = mount('<ry-bento><div>a</div><div>b</div><div>c</div></ry-bento>');
 * const cells = el.querySelectorAll('[data-ry-target="cell"]');
 * await expectEqual(cells.length, 3, 'three cells');
 * await expectEqual(cells[1].style.left, '33.3333%', 'second cell starts at 1/3');
 * await expectEqual(cells[1].style.height, '100%', 'columns are full height');
 * ```
 *
 * @tutorial explicit-percent-rects
 * ```js
 * const el = mount('<ry-bento><div cell="0 0 30 100">a</div><div cell="30 0 70 100">b</div></ry-bento>');
 * const cells = el.querySelectorAll('[data-ry-target="cell"]');
 * await expectEqual(cells[0].style.width, '30%', 'explicit width honored');
 * await expectEqual(cells[1].style.left, '30%', 'explicit x honored');
 * ```
 *
 * @tutorial invalid-rects-fall-back-to-auto-tile
 * ```js
 * const el = mount('<ry-bento><div cell="0 0 80 100">a</div><div cell="30 0 70 100">b</div></ry-bento>');
 * // 80+70 overlaps → the set is rejected, children auto-tile 50/50.
 * await expectEqual(el.querySelector('[data-ry-target="cell"]').style.width, '50%', 'fell back to equal columns');
 * ```
 *
 * @tutorial resizable-derives-gutters
 * ```js
 * const el = mount('<ry-bento resizable><div cell="0 0 50 50">a</div><div cell="50 0 50 50">b</div><div cell="0 50 50 50">c</div><div cell="50 50 50 50">d</div></ry-bento>');
 * // 2×2 grid: 4 rail-anchored segments (2 vertical + 2 horizontal), each independent.
 * await expectEqual(el.querySelectorAll('[data-ry-target="gutter"]').length, 4, '4 gutters');
 * const g = el.querySelector('[data-ry-target="gutter"]');
 * await expectAttr(g, 'role', 'separator');
 * ```
 *
 * @tutorial split-at-runtime
 * ```js
 * const el = mount('<ry-bento><div>a</div><div>b</div></ry-bento>');
 * const first = el.querySelector('[data-ry-target="cell"]');
 * const fresh = el.splitCell(first, 'row');
 * await expectEqual(el.querySelectorAll('[data-ry-target="cell"]').length, 3, 'split added a cell');
 * await expectEqual(fresh.style.top, '50%', 'new cell is the bottom half');
 * ```
 *
 * @tutorial remove-absorbs-space
 * ```js
 * const el = mount('<ry-bento><div>a</div><div>b</div></ry-bento>');
 * const first = el.querySelector('[data-ry-target="cell"]');
 * await expectEqual(el.removeCell(first), true, 'removable');
 * await expectEqual(el.querySelector('[data-ry-target="cell"]').style.width, '100%', 'neighbor absorbed the space');
 * ```
 *
 * @tutorial editable-mounts-chrome
 * ```js
 * const el = mount('<ry-bento editable><div>a</div><div>b</div></ry-bento>');
 * const cell = el.querySelector('[data-ry-target="cell"]');
 * await expectEqual(cell.querySelectorAll('[data-ry-target="edge-zone"]').length, 4, 'four split zones per cell');
 * await expectEqual(cell.querySelectorAll('[data-ry-target="remove-button"]').length, 1, 'one remove button');
 * await expectEqual(cell.querySelectorAll('[data-ry-target="drag-handle"]').length, 1, 'one drag handle');
 * cell.querySelector('[data-side="bottom"] [data-ry-target="split-button"]').click();
 * await expectEqual(el.querySelectorAll('[data-ry-target="cell"]').length, 3, '+ split the cell');
 * ```
 *
 * @tutorial lockable-toggle
 * ```js
 * const el = mount('<ry-bento editable lockable><div>a</div><div>b</div></ry-bento>');
 * const lock = el.querySelector('[data-ry-target="lock-button"]');
 * await expectEqual(lock.getAttribute('aria-pressed'), 'true', 'editing state');
 * lock.click();
 * await expectEqual(el.hasAttribute('editable'), false, 'locked');
 * await expectEqual(el.querySelector('[data-ry-target="edge-zone"]'), null, 'chrome unmounted');
 * lock.click();
 * await expectEqual(el.querySelectorAll('[data-ry-target="edge-zone"]').length, 8, 'chrome back');
 * ```
 *
 * @tutorial swap-exchanges-rects
 * ```js
 * const el = mount('<ry-bento><div cell="0 0 30 100">a</div><div cell="30 0 70 100">b</div></ry-bento>');
 * const [a, b] = el.querySelectorAll('[data-ry-target="cell"]');
 * await expectEqual(el.swapCells(a, b), true, 'swappable');
 * await expectEqual(a.style.width, '70%', 'a took b’s rect');
 * await expectEqual(b.style.left, '0%', 'b took a’s rect');
 * ```
 *
 * @tutorial beforesplit-veto
 * ```js
 * // preventDefault() on ry:beforesplit keeps the grid as-is — splitCell()
 * // returns null and ry:split stays silent. Lift the veto and it splits.
 * const el = mount('<ry-bento><div>a</div><div>b</div></ry-bento>');
 * const first = el.querySelector('[data-ry-target="cell"]');
 * const splits = [];
 * el.addEventListener('ry:split', () => splits.push(1));
 * el.addEventListener('ry:beforesplit', (e) => e.preventDefault(), { once: true });
 * await expectEqual(el.splitCell(first, 'row'), null, 'vetoed split returns null');
 * await expectEqual(el.querySelectorAll('[data-ry-target="cell"]').length, 2, 'no cell added');
 * await expectEqual(splits.length, 0, 'ry:split did not fire');
 * await expectEqual(el.splitCell(first, 'row') !== null, true, 'without the veto, split works');
 * await expectEqual(el.querySelectorAll('[data-ry-target="cell"]').length, 3);
 * ```
 *
 * @tutorial beforeremove-veto
 * ```js
 * // preventDefault() on ry:beforeremove keeps the cell — removeCell() returns
 * // false, the DOM and layout are untouched, ry:remove stays silent.
 * const el = mount('<ry-bento><div>a</div><div>b</div></ry-bento>');
 * const first = el.querySelector('[data-ry-target="cell"]');
 * const removed = [];
 * el.addEventListener('ry:remove', () => removed.push(1));
 * el.addEventListener('ry:beforeremove', (e) => e.preventDefault(), { once: true });
 * await expectEqual(el.removeCell(first), false, 'vetoed remove returns false');
 * await expectEqual(el.querySelectorAll('[data-ry-target="cell"]').length, 2, 'cell still there');
 * await expectEqual(first.style.width, '50%', 'layout untouched');
 * await expectEqual(removed.length, 0, 'ry:remove did not fire');
 * await expectEqual(el.removeCell(first), true, 'without the veto, remove works');
 * ```
 *
 * @tutorial beforeswap-veto
 * ```js
 * // preventDefault() on ry:beforeswap leaves both rects alone — swapCells()
 * // returns false and ry:swap stays silent.
 * const el = mount('<ry-bento><div cell="0 0 30 100">a</div><div cell="30 0 70 100">b</div></ry-bento>');
 * const [a, b] = el.querySelectorAll('[data-ry-target="cell"]');
 * const swaps = [];
 * el.addEventListener('ry:swap', () => swaps.push(1));
 * el.addEventListener('ry:beforeswap', (e) => e.preventDefault(), { once: true });
 * await expectEqual(el.swapCells(a, b), false, 'vetoed swap returns false');
 * await expectEqual(a.style.width, '30%', 'a kept its rect');
 * await expectEqual(swaps.length, 0, 'ry:swap did not fire');
 * await expectEqual(el.swapCells(a, b), true, 'without the veto, swap works');
 * await expectEqual(a.style.width, '70%', 'a took b’s rect');
 * ```
 *
 * @see ry-n-split
 */

import { RyElement } from '../core/ry-element.js';

// ═══════════════════════════════════════════════════════════════
// Engine — pure functions over percent rects. Direct port of the
// blitzen2d "absolute" bento engine; exported for unit tests.
// ═══════════════════════════════════════════════════════════════

export interface BentoCell {
  id: string;
  /** Percent rect, 0–100 in both axes. Cells touch exactly (no gap in storage). */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BentoEdge {
  id: string;
  axis: 'col' | 'row';
  /** Px along the edge's axis (col → x, row → y). */
  coord: number;
  /** Px range perpendicular to the axis. */
  extent: [number, number];
  /** Raw segment ids merged into this edge; length > 1 means it cascades. */
  cascadeGroup: string[];
}

interface Size {
  w: number;
  h: number;
}

const EPS = 0.05; // percent-space tolerance
const MIN_CELL_PCT = 5;

/** Null if cells tile cleanly (no overlap, area = 100×100); else a description. */
export function bentoValidate(cells: BentoCell[]): string | null {
  for (let i = 0; i < cells.length; i++) {
    for (let j = i + 1; j < cells.length; j++) {
      const a = cells[i]!;
      const b = cells[j]!;
      const xOv = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
      const yOv = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
      if (xOv > EPS && yOv > EPS) return `overlap ${a.id}/${b.id}`;
    }
  }
  const total = cells.reduce((s, c) => s + c.w * c.h, 0);
  if (Math.abs(total - 10000) > 0.5) return `area ${total.toFixed(2)}/10000`;
  return null;
}

/**
 * Draggable edges in PX space, derived from rect adjacency and rail-merged.
 * Rail rule: collinear segments stay independent iff perpendicular boundaries
 * exist on BOTH sides of the shared endpoint; otherwise they cascade as one.
 */
export function bentoEdges(cells: BentoCell[], container: Size): BentoEdge[] {
  const raw: Array<Omit<BentoEdge, 'cascadeGroup'>> = [];
  for (let i = 0; i < cells.length; i++) {
    for (let j = i + 1; j < cells.length; j++) {
      const a = cells[i]!;
      const b = cells[j]!;
      // a.right == b.left / b.right == a.left
      for (const [l, r] of [[a, b], [b, a]] as const) {
        if (Math.abs(l.x + l.w - r.x) <= EPS) {
          const s = Math.max(l.y, r.y);
          const e = Math.min(l.y + l.h, r.y + r.h);
          if (e - s > EPS) raw.push({ id: `e${i}_${j}_v`, axis: 'col', coord: l.x + l.w, extent: [s, e] });
        }
        if (Math.abs(l.y + l.h - r.y) <= EPS) {
          const s = Math.max(l.x, r.x);
          const e = Math.min(l.x + l.w, r.x + r.w);
          if (e - s > EPS) raw.push({ id: `e${i}_${j}_h`, axis: 'row', coord: l.y + l.h, extent: [s, e] });
        }
      }
    }
  }
  const px = raw.map((e) => ({
    ...e,
    coord: e.axis === 'col' ? (e.coord / 100) * container.w : (e.coord / 100) * container.h,
    extent: (e.axis === 'col'
      ? [(e.extent[0] / 100) * container.h, (e.extent[1] / 100) * container.h]
      : [(e.extent[0] / 100) * container.w, (e.extent[1] / 100) * container.w]) as [number, number],
  }));
  return mergeWithRails(px);
}

function mergeWithRails(all: Array<Omit<BentoEdge, 'cascadeGroup'>>): BentoEdge[] {
  const COORD_EPS = 0.5;
  const byKey = new Map<string, Array<Omit<BentoEdge, 'cascadeGroup'>>>();
  for (const e of all) {
    const key = `${e.axis}@${e.coord.toFixed(1)}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(e);
  }
  const out: BentoEdge[] = [];
  for (const segs of byKey.values()) {
    segs.sort((a, b) => a.extent[0] - b.extent[0]);
    let cur: BentoEdge = { ...segs[0]!, extent: [...segs[0]!.extent], cascadeGroup: [segs[0]!.id] };
    for (let i = 1; i < segs.length; i++) {
      const nxt = segs[i]!;
      if (!railInGap(all, cur.axis, cur.coord, cur.extent[1], nxt.extent[0], COORD_EPS)) {
        cur.extent[1] = nxt.extent[1];
        cur.cascadeGroup.push(nxt.id);
      } else {
        out.push(cur);
        cur = { ...nxt, extent: [...nxt.extent], cascadeGroup: [nxt.id] };
      }
    }
    out.push(cur);
  }
  return out;
}

function railInGap(
  all: Array<Omit<BentoEdge, 'cascadeGroup'>>,
  axis: 'col' | 'row',
  coord: number,
  gapStart: number,
  gapEnd: number,
  eps: number,
): boolean {
  const perps = all.filter((o) => o.axis !== axis && o.coord >= gapStart - eps && o.coord <= gapEnd + eps);
  if (!perps.length) return false;
  const byCoord = new Map<number, typeof perps>();
  for (const p of perps) {
    const key = Math.round(p.coord * 10) / 10;
    if (!byCoord.has(key)) byCoord.set(key, []);
    byCoord.get(key)!.push(p);
  }
  for (const group of byCoord.values()) {
    const onA = group.some((p) => p.extent[1] <= coord + eps);
    const onB = group.some((p) => p.extent[0] >= coord - eps);
    if (onA && onB) return true;
  }
  return false;
}

function minPctForAxis(axis: 'col' | 'row', container: Size, minPx: number): number {
  const dim = axis === 'col' ? container.w : container.h;
  return Math.max(MIN_CELL_PCT, (minPx / dim) * 100);
}

export interface SlideResult {
  cells: BentoCell[];
  ok: boolean;
  clamped: boolean;
  /** The px delta actually applied after clamping. */
  actualDelta: number;
}

/**
 * Move an edge by deltaPx. Applies a percent delta to every cell touching the
 * edge within its extent (growing one side, shrinking the other), clamped so
 * no cell goes below the min. Refuses (ok:false) if the result breaks tiling.
 */
export function bentoSlide(
  cells: BentoCell[],
  edgeId: string,
  deltaPx: number,
  container: Size,
  minPx = 50,
): SlideResult {
  const e = bentoEdges(cells, container).find((x) => x.id === edgeId);
  if (!e) return { cells, ok: false, clamped: false, actualDelta: 0 };
  const coordPct = e.axis === 'col' ? (e.coord / container.w) * 100 : (e.coord / container.h) * 100;
  const extentPct: [number, number] = e.axis === 'col'
    ? [(e.extent[0] / container.h) * 100, (e.extent[1] / container.h) * 100]
    : [(e.extent[0] / container.w) * 100, (e.extent[1] / container.w) * 100];
  const deltaPct = e.axis === 'col' ? (deltaPx / container.w) * 100 : (deltaPx / container.h) * 100;

  const growSide = new Set<BentoCell>();
  const shrinkSide = new Set<BentoCell>();
  for (const c of cells) {
    const [pos, size, perpPos, perpSize]: [number, number, number, number] = e.axis === 'col'
      ? [c.x, c.w, c.y, c.h] : [c.y, c.h, c.x, c.w];
    const overlaps = perpPos < extentPct[1] - EPS && perpPos + perpSize > extentPct[0] + EPS;
    if (!overlaps) continue;
    if (Math.abs(pos + size - coordPct) <= EPS) growSide.add(c);
    if (Math.abs(pos - coordPct) <= EPS) shrinkSide.add(c);
  }

  // Positive delta grows growSide and shrinks shrinkSide; clamp each
  // direction by the cells that would shrink.
  const minPct = minPctForAxis(e.axis, container, minPx);
  let maxPos = Infinity;
  let maxNeg = -Infinity;
  for (const c of shrinkSide) maxPos = Math.min(maxPos, (e.axis === 'col' ? c.w : c.h) - minPct);
  for (const c of growSide) maxNeg = Math.max(maxNeg, -((e.axis === 'col' ? c.w : c.h) - minPct));
  let clamped = false;
  let d = deltaPct;
  if (d > maxPos) { d = maxPos; clamped = true; }
  if (d < maxNeg) { d = maxNeg; clamped = true; }

  const next = cells.map((c) => {
    if (growSide.has(c)) return e.axis === 'col' ? { ...c, w: c.w + d } : { ...c, h: c.h + d };
    if (shrinkSide.has(c)) return e.axis === 'col'
      ? { ...c, x: c.x + d, w: c.w - d }
      : { ...c, y: c.y + d, h: c.h - d };
    return c;
  });

  if (bentoValidate(next)) return { cells, ok: false, clamped, actualDelta: 0 };
  return {
    cells: next,
    ok: true,
    clamped,
    actualDelta: e.axis === 'col' ? (d / 100) * container.w : (d / 100) * container.h,
  };
}

/** Split a cell in half along `axis`; the new rect goes on `side`. */
export function bentoSplit(
  cells: BentoCell[],
  cellId: string,
  axis: 'col' | 'row',
  side: 'before' | 'after',
  newId: string,
): { cells: BentoCell[]; ok: boolean } {
  const cell = cells.find((c) => c.id === cellId);
  if (!cell) return { cells, ok: false };
  const half = (axis === 'col' ? cell.w : cell.h) / 2;
  if (half < MIN_CELL_PCT) return { cells, ok: false };
  const first = axis === 'col' ? { ...cell, w: half } : { ...cell, h: half };
  const second = axis === 'col'
    ? { ...cell, x: cell.x + half, w: half }
    : { ...cell, y: cell.y + half, h: half };
  const keep = side === 'after' ? { ...first, id: cellId } : { ...second, id: cellId };
  const fresh = side === 'after' ? { ...second, id: newId } : { ...first, id: newId };
  return { cells: cells.map((c) => (c.id === cellId ? keep : c)).concat([fresh]), ok: true };
}

const ABSORB_EPS = 0.5;
const SIDES = ['right', 'bottom', 'left', 'top'] as const;
type Side = (typeof SIDES)[number];

export function bentoCanRemove(cells: BentoCell[], cellId: string): boolean {
  const cell = cells.find((c) => c.id === cellId);
  if (!cell) return false;
  const others = cells.filter((c) => c.id !== cellId);
  return SIDES.some((s) => canAbsorbFrom(others, cell, s));
}

/** Remove a cell; neighbors on one clean side grow to absorb its space. */
export function bentoRemove(cells: BentoCell[], cellId: string): { cells: BentoCell[]; ok: boolean } {
  const cell = cells.find((c) => c.id === cellId);
  if (!cell) return { cells, ok: false };
  const others = cells.filter((c) => c.id !== cellId);
  for (const side of SIDES) {
    if (!canAbsorbFrom(others, cell, side)) continue;
    const neighbors = neighborsOnSide(others, cell, side);
    const next = others.map((c) => {
      if (!neighbors.includes(c)) return c;
      switch (side) {
        case 'right': return { ...c, x: cell.x, w: c.w + cell.w };
        case 'left': return { ...c, w: c.w + cell.w };
        case 'bottom': return { ...c, y: cell.y, h: c.h + cell.h };
        case 'top': return { ...c, h: c.h + cell.h };
      }
    });
    if (!bentoValidate(next)) return { cells: next, ok: true };
  }
  return { cells, ok: false };
}

function canAbsorbFrom(others: BentoCell[], cell: BentoCell, side: Side): boolean {
  const neighbors = neighborsOnSide(others, cell, side);
  if (!neighbors.length) return false;
  return side === 'right' || side === 'left'
    ? extentCovers(neighbors, cell.y, cell.y + cell.h, 'y', 'h')
    : extentCovers(neighbors, cell.x, cell.x + cell.w, 'x', 'w');
}

function neighborsOnSide(cells: BentoCell[], cell: BentoCell, side: Side): BentoCell[] {
  return cells.filter((c) => {
    switch (side) {
      case 'right':
        return Math.abs(c.x - (cell.x + cell.w)) <= ABSORB_EPS
          && c.y >= cell.y - ABSORB_EPS && c.y + c.h <= cell.y + cell.h + ABSORB_EPS;
      case 'left':
        return Math.abs(c.x + c.w - cell.x) <= ABSORB_EPS
          && c.y >= cell.y - ABSORB_EPS && c.y + c.h <= cell.y + cell.h + ABSORB_EPS;
      case 'bottom':
        return Math.abs(c.y - (cell.y + cell.h)) <= ABSORB_EPS
          && c.x >= cell.x - ABSORB_EPS && c.x + c.w <= cell.x + cell.w + ABSORB_EPS;
      case 'top':
        return Math.abs(c.y + c.h - cell.y) <= ABSORB_EPS
          && c.x >= cell.x - ABSORB_EPS && c.x + c.w <= cell.x + cell.w + ABSORB_EPS;
    }
  });
}

/**
 * FLIP layout transition (First/Last/Invert/Play — ported from blitzen2d
 * workspace/flip.js). Measures every cell, runs the synchronous mutation,
 * then counter-transforms each cell from its old rect and animates to
 * identity. Fresh cells (not in the before-set) scale/fade in.
 *
 * Opt in globally: `RyBento.animator = bentoFlip`.
 */
export function bentoFlip(root: HTMLElement, mutate: () => void, duration = 280): void {
  const SPRING = 'cubic-bezier(0.32, 0.72, 0, 1)';
  const cellsOf = () => [...root.querySelectorAll<HTMLElement>('[data-ry-target="cell"]')];
  const before = new Map<HTMLElement, DOMRect>();
  for (const el of cellsOf()) before.set(el, el.getBoundingClientRect());
  mutate();
  for (const el of cellsOf()) {
    if (typeof el.animate !== 'function') return; // no WAAPI (headless) — mutation already applied
    const after = el.getBoundingClientRect();
    const b = before.get(el);
    if (!b) {
      el.animate(
        [{ opacity: 0, transform: 'scale(0.92)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration, easing: SPRING },
      );
      continue;
    }
    const dx = b.left - after.left;
    const dy = b.top - after.top;
    const sx = b.width / Math.max(after.width, 1);
    const sy = b.height / Math.max(after.height, 1);
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) continue;
    el.animate(
      [
        { transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
        { transformOrigin: '0 0', transform: 'translate(0, 0) scale(1, 1)' },
      ],
      { duration, easing: SPRING },
    );
  }
}

/** Nearest snap target within `radius` of `cursor`, or `cursor` unchanged. */
export function bentoSnap(targets: number[], cursor: number, radius: number): number {
  let best = radius + 1;
  let out = cursor;
  for (const t of targets) {
    const d = Math.abs(t - cursor);
    if (d <= radius && d < best) { best = d; out = t; }
  }
  return out;
}

function extentCovers(cells: BentoCell[], low: number, high: number, axis: 'x' | 'y', sizeKey: 'w' | 'h'): boolean {
  const sorted = cells.slice().sort((a, b) => a[axis] - b[axis]);
  let cursor = low;
  for (const c of sorted) {
    if (c[axis] > cursor + ABSORB_EPS) return false;
    cursor = Math.max(cursor, c[axis] + c[sizeKey]);
  }
  return cursor >= high - ABSORB_EPS;
}

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

const GUTTER_PX = 12;
const round4 = (n: number): number => parseFloat(n.toFixed(4));

/** Chrome children don't count as cell content for the data-ry-empty check. */
const CHROME_TARGETS = new Set(['remove-button', 'drag-handle', 'edge-zone']);

/** Edit-chrome edge map (blitzen2d edge-buttons.js): which axis/side each cell edge splits. */
const EDGE_DEFS = [
  { side: 'top', axis: 'row', place: 'before' },
  { side: 'right', axis: 'col', place: 'after' },
  { side: 'bottom', axis: 'row', place: 'after' },
  { side: 'left', axis: 'col', place: 'before' },
] as const;

export class RyBento extends RyElement {
  /**
   * Layout-transition hook for structural changes (split/remove/swap).
   * null = instant. Set to `bentoFlip` (exported) or any function that
   * measures, runs `mutate()` synchronously, and plays its own animation.
   * Static on purpose — layout motion should feel identical app-wide.
   */
  static animator: ((root: HTMLElement, mutate: () => void) => void) | null = null;

  static get observedAttributes(): string[] {
    return ['editable', 'resizable', 'lockable'];
  }

  #cells: BentoCell[] = [];
  #els = new Map<string, HTMLElement>();
  #nextId = 0;
  #resizeObserver: ResizeObserver | null = null;
  #chromed = new WeakSet<HTMLElement>();

  setup(): void {
    this.#readCells();
    this.#restore();
    this.#paint();
    this.#mountGutters();
    if (typeof ResizeObserver !== 'undefined') {
      // Cells are percent-positioned (responsive for free), but gutter
      // strips are px overlays — reproject them when the host resizes.
      this.#resizeObserver = new ResizeObserver(() => this.#mountGutters());
      this.#resizeObserver.observe(this);
    }
    this.#mountChrome();
    this.#mountLock();
    // Self-heal: if an app re-rendered a cell's content and wiped the
    // chrome, restore it when the pointer re-enters the bento.
    this.on(this, 'pointerenter', () => this.#mountChrome());
    this.#markEmpty();
    // Keep data-ry-empty live: apps may fill (or clear) a cell at any time.
    this.observe(() => this.#markEmpty(), { childList: true, subtree: true, characterData: true });
  }

  // Lock/edit toggle: [editable] and [resizable] are live — flipping them
  // mounts/unmounts the chrome and gutters (both are self-gating).
  attributeChangedCallback(name: string, oldV: string | null, newV: string | null): void {
    if (oldV === newV || !this.hasAttribute('data-ry-initialized')) return;
    this.#mountGutters();
    this.#mountChrome();
    if (name === 'lockable') this.#mountLock();
    else this.#syncLock();
  }

  // ── Lock button ([lockable]: the bento's own edit-mode switch) ──

  #mountLock(): void {
    for (const old of [...this.children]) {
      if (old.getAttribute('data-ry-target') === 'lock-button') old.remove();
    }
    if (!this.hasAttribute('lockable')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-ry-target', 'lock-button');
    btn.className = 'ry-bento__lock';
    btn.addEventListener('click', () => this.toggleAttribute('editable'));
    this.appendChild(btn);
    this.#syncLock();
  }

  #syncLock(): void {
    const btn = [...this.children].find(
      (c) => c.getAttribute('data-ry-target') === 'lock-button',
    ) as HTMLButtonElement | undefined;
    if (!btn) return;
    const editing = this.hasAttribute('editable');
    btn.textContent = editing ? '✓' : '✎';
    btn.title = editing ? 'Done — lock layout' : 'Edit layout';
    btn.setAttribute('aria-label', btn.title);
    btn.setAttribute('aria-pressed', String(editing));
  }

  teardown(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get layout(): Array<{ x: number; y: number; w: number; h: number }> {
    return this.#cells.map(({ x, y, w, h }) => ({ x, y, w, h }));
  }

  // ── Public runtime ops ──

  splitCell(cell: HTMLElement, axis: 'col' | 'row' = 'col', side: 'before' | 'after' = 'after'): HTMLElement | null {
    const id = this.#idOf(cell);
    if (!id) return null;
    const newId = `c${this.#nextId++}`;
    const r = bentoSplit(this.#cells, id, axis, side, newId);
    if (!r.ok) return null;
    if (!this.emit('beforesplit', { cell, axis, side })) return null;
    const fresh = document.createElement(cell.tagName.toLowerCase());
    fresh.setAttribute('data-ry-target', 'cell');
    // Convention over config: a new panel should look like its sibling.
    // If the split cell wraps a single element (e.g. a ry-card), seed the
    // fresh cell with an empty shallow clone of it — same tag/class/style,
    // blank inside. No single wrapper → fresh stays empty (dashed slot).
    const wrappers = [...cell.children].filter(
      (k) => !CHROME_TARGETS.has(k.getAttribute('data-ry-target') ?? ''),
    );
    if (wrappers.length === 1) {
      const seed = wrappers[0]!.cloneNode(false) as HTMLElement;
      // Strip harness state the clone must not inherit (initialized flags etc.).
      for (const a of [...seed.attributes]) {
        if (a.name.startsWith('data-ry-')) seed.removeAttribute(a.name);
      }
      fresh.appendChild(seed);
    }
    this.#animate(() => {
      cell.insertAdjacentElement(side === 'after' ? 'afterend' : 'beforebegin', fresh);
      this.#els.set(newId, fresh);
      // Emit BEFORE #commit so a listener can fill detail.cell (even via
      // innerHTML/textContent) without wiping the edit chrome commit mounts.
      this.emit('split', { cell: fresh });
      this.#commit(this.#orderByDom(r.cells));
    });
    return fresh;
  }

  removeCell(cell: HTMLElement): boolean {
    const id = this.#idOf(cell);
    if (!id) return false;
    const r = bentoRemove(this.#cells, id);
    if (!r.ok) return false;
    const layout = r.cells.map(({ x, y, w, h }) => ({ x, y, w, h }));
    if (!this.emit('beforeremove', { cell, layout })) return false;
    this.#animate(() => {
      cell.remove();
      this.#els.delete(id);
      this.#commit(r.cells);
      this.emit('remove', { layout: this.layout });
    });
    return true;
  }

  canRemoveCell(cell: HTMLElement): boolean {
    const id = this.#idOf(cell);
    return id ? bentoCanRemove(this.#cells, id) : false;
  }

  swapCells(a: HTMLElement, b: HTMLElement): boolean {
    const ia = this.#idOf(a);
    const ib = this.#idOf(b);
    if (!ia || !ib || ia === ib) return false;
    const ca = this.#cells.find((c) => c.id === ia)!;
    const cb = this.#cells.find((c) => c.id === ib)!;
    if (!this.emit('beforeswap', { a, b })) return false;
    this.#animate(() => {
      // Each element keeps its content and takes the OTHER's geometry
      // (blitzen2d swap-cells: panels travel as units).
      this.#commit(this.#cells.map((c) => {
        if (c.id === ia) return { ...c, x: cb.x, y: cb.y, w: cb.w, h: cb.h };
        if (c.id === ib) return { ...c, x: ca.x, y: ca.y, w: ca.w, h: ca.h };
        return c;
      }));
      this.emit('swap', { a, b });
    });
    return true;
  }

  /** Route a structural mutation through the pluggable animator. */
  #animate(mutate: () => void): void {
    const anim = (this.constructor as typeof RyBento).animator;
    if (anim) anim(this, mutate);
    else mutate();
  }

  // ── Declarative read ──

  #readCells(): void {
    const children = [...this.children].filter(
      (c) => c.getAttribute('data-ry-target') !== 'gutter',
    ) as HTMLElement[];
    const parsed = children.map((el) => {
      const attr = el.getAttribute('cell');
      if (!attr) return null;
      const n = attr.trim().split(/[\s,]+/).map(parseFloat);
      return n.length === 4 && n.every(Number.isFinite)
        ? { x: n[0]!, y: n[1]!, w: n[2]!, h: n[3]! }
        : null;
    });

    let rects: Array<{ x: number; y: number; w: number; h: number }>;
    if (children.length && parsed.every((p) => p !== null)) {
      rects = parsed as Array<{ x: number; y: number; w: number; h: number }>;
      const violation = bentoValidate(rects.map((r, i) => ({ id: `c${i}`, ...r })));
      if (violation) {
        console.warn(`[ry-bento] cell rects don't tile (${violation}) — auto-tiling equal columns`);
        rects = children.map((_, i) => ({ x: (i * 100) / children.length, y: 0, w: 100 / children.length, h: 100 }));
      }
    } else {
      rects = children.map((_, i) => ({ x: (i * 100) / children.length, y: 0, w: 100 / children.length, h: 100 }));
    }

    this.#cells = children.map((el, i) => {
      el.setAttribute('data-ry-target', 'cell');
      const id = `c${this.#nextId++}`;
      this.#els.set(id, el);
      return { id, ...rects[i]! };
    });
  }

  #idOf(el: HTMLElement): string | null {
    for (const [id, e] of this.#els) if (e === el) return id;
    return null;
  }

  /** Keep #cells in DOM child order so persist-by-index stays stable. */
  #orderByDom(cells: BentoCell[]): BentoCell[] {
    const order = new Map<string, number>();
    let i = 0;
    for (const child of this.children) {
      if (child.getAttribute('data-ry-target') !== 'cell') continue;
      const id = this.#idOf(child as HTMLElement);
      if (id) order.set(id, i++);
    }
    return cells.slice().sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }

  // ── Layout ──

  #paint(cells: BentoCell[] = this.#cells): void {
    for (const c of cells) {
      const el = this.#els.get(c.id);
      if (!el) continue;
      el.style.left = `${round4(c.x)}%`;
      el.style.top = `${round4(c.y)}%`;
      el.style.width = `${round4(c.w)}%`;
      el.style.height = `${round4(c.h)}%`;
    }
  }

  #size(): Size {
    // Layout dims (transform-immune), with a nominal fallback for
    // display:none / headless environments so gutters still mount.
    return { w: this.offsetWidth || 1000, h: this.offsetHeight || 800 };
  }

  #commit(cells: BentoCell[]): void {
    this.#cells = cells;
    this.#paint();
    this.#persist();
    this.#mountGutters();
    this.#mountChrome(); // fresh cells need chrome (self-gating)
    this.#markEmpty();
  }

  /** Mark cells whose only children are edit chrome — theme shows them as empty slots. */
  #markEmpty(): void {
    for (const c of this.#cells) {
      const el = this.#els.get(c.id);
      if (!el) continue;
      const hasContent = [...el.childNodes].some((n) => {
        if (n.nodeType === Node.TEXT_NODE) return (n.textContent ?? '').trim() !== '';
        if (n.nodeType === Node.ELEMENT_NODE) return !CHROME_TARGETS.has((n as Element).getAttribute('data-ry-target') ?? '');
        return false;
      });
      el.toggleAttribute('data-ry-empty', !hasContent);
    }
  }

  // ── Edit chrome (ported from blitzen2d edge-buttons.js + panel-menu.js) ──
  // Per cell: four hover edge zones whose `+` splits on that axis, and a
  // corner `✕` that removes — disabled when no clean absorber exists.
  // Idempotent by DOM check (not a one-shot flag): if an app re-renders a
  // cell's innerHTML and wipes the chrome, the next commit or host hover
  // restores it.
  #mountChrome(): void {
    const editable = this.hasAttribute('editable');
    for (const c of this.#cells) {
      const el = this.#els.get(c.id);
      if (!el) continue;
      if (!editable) {
        // Locked: strip the chrome. Cell-level listeners (in #chromed) stay
        // attached but no-op without chrome nodes to find.
        for (const k of [...el.children]) {
          if (CHROME_TARGETS.has(k.getAttribute('data-ry-target') ?? '')) k.remove();
        }
        continue;
      }
      if ([...el.children].some((k) => k.getAttribute('data-ry-target') === 'remove-button')) continue;

      if (!this.#chromed.has(el)) {
        this.#chromed.add(el);
        // Cell-level listeners attach ONCE; they look up the current chrome
        // nodes dynamically since chrome may be rebuilt after a content wipe.
        el.addEventListener('pointerenter', () => {
          const rm = el.querySelector<HTMLButtonElement>('[data-ry-target="remove-button"]');
          if (!rm) return;
          // Absorbability depends on the whole layout — recheck on each hover.
          const ok = this.canRemoveCell(el);
          rm.disabled = !ok;
          rm.title = ok ? 'Remove cell' : 'Cannot remove — no neighbor cleanly absorbs this space';
        });
      }

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.setAttribute('data-ry-target', 'remove-button');
      rm.className = 'ry-bento__remove';
      rm.setAttribute('aria-label', 'Remove cell');
      rm.textContent = '✕';
      rm.title = 'Remove cell';
      rm.addEventListener('click', (e) => { e.stopPropagation(); this.removeCell(el); });
      el.appendChild(rm);

      const dh = document.createElement('button');
      dh.type = 'button';
      dh.setAttribute('data-ry-target', 'drag-handle');
      dh.className = 'ry-bento__drag';
      dh.setAttribute('aria-label', 'Move cell — drop on another cell to swap');
      dh.title = 'Drag onto another cell to swap';
      dh.textContent = '⠿';
      dh.addEventListener('pointerdown', (e) => this.#startSwapDrag(e, el));
      el.appendChild(dh);

      for (const d of EDGE_DEFS) {
        const zone = document.createElement('div');
        zone.setAttribute('data-ry-target', 'edge-zone');
        zone.className = 'ry-bento__edge-zone';
        zone.dataset.side = d.side;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-ry-target', 'split-button');
        btn.className = 'ry-bento__split';
        btn.setAttribute('aria-label', `Add cell (${d.side})`);
        btn.textContent = '+';
        zone.appendChild(btn);
        // The + tracks the cursor along the edge so it feels alive.
        zone.addEventListener('pointermove', (e) => {
          const r = zone.getBoundingClientRect();
          if (d.side === 'top' || d.side === 'bottom') btn.style.left = `${e.clientX - r.left}px`;
          else btn.style.top = `${e.clientY - r.top}px`;
        });
        zone.addEventListener('pointerleave', () => { btn.style.left = ''; btn.style.top = ''; });
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.splitCell(el, d.axis, d.place); });
        el.appendChild(zone);
      }
    }
  }

  // ── Gutters (overlay strips at derived edges) ──

  #mountGutters(): void {
    for (const old of [...this.children]) {
      if (old.getAttribute('data-ry-target') === 'gutter') old.remove();
    }
    if (!this.hasAttribute('resizable')) return; // strips stay gone
    // A lockable bento is FULLY frozen while locked — no resize either.
    if (this.hasAttribute('lockable') && !this.hasAttribute('editable')) return;
    const container = this.#size();
    for (const edge of bentoEdges(this.#cells, container)) {
      const g = document.createElement('div');
      g.setAttribute('data-ry-target', 'gutter');
      g.className = 'ry-bento__gutter';
      g.setAttribute('role', 'separator');
      g.setAttribute('aria-orientation', edge.axis === 'col' ? 'vertical' : 'horizontal');
      g.setAttribute('tabindex', '0');
      g.dataset.edgeId = edge.id;
      if (edge.axis === 'col') {
        g.style.left = `${edge.coord - GUTTER_PX / 2}px`;
        g.style.top = `${edge.extent[0]}px`;
        g.style.width = `${GUTTER_PX}px`;
        g.style.height = `${edge.extent[1] - edge.extent[0]}px`;
      } else {
        g.style.top = `${edge.coord - GUTTER_PX / 2}px`;
        g.style.left = `${edge.extent[0]}px`;
        g.style.height = `${GUTTER_PX}px`;
        g.style.width = `${edge.extent[1] - edge.extent[0]}px`;
      }
      this.appendChild(g);
      // Listeners live on the gutter element itself (NOT via this.on()) —
      // gutters remount on every commit/resize, and per-remount cleanup fns
      // would pile up in the base class until disconnect.
      g.addEventListener('pointerdown', (e) => this.#startDrag(e, g, edge));
      g.addEventListener('keydown', (e) => this.#onKey(e, edge));
    }
  }

  // ── Swap drag (ported from blitzen2d workspace/panel-drag.js) ──
  // Grab the ⠿ handle, the cell lifts and follows the cursor; the cell
  // under the pointer highlights as the swap target; drop = swapCells.
  #startSwapDrag(e: PointerEvent, cell: HTMLElement): void {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    let dragging = false;
    let target: HTMLElement | null = null;

    const setTarget = (next: HTMLElement | null) => {
      if (target === next) return;
      target?.classList.remove('is-swap-target');
      target = next;
      target?.classList.add('is-swap-target');
    };

    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return; // click ≠ drag
        dragging = true;
        cell.classList.add('is-dragging');
      }
      cell.style.transform = `translate(${dx}px, ${dy}px)`;
      // Peek under the lifted cell for the drop target.
      cell.style.pointerEvents = 'none';
      const under = document.elementFromPoint(ev.clientX, ev.clientY);
      cell.style.pointerEvents = '';
      const other = (under?.closest?.('[data-ry-target="cell"]') ?? null) as HTMLElement | null;
      setTarget(other && other !== cell && other.parentElement === this ? other : null);
    };
    const stop = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', stop);
      document.removeEventListener('pointercancel', stop);
      cell.style.transform = '';
      cell.classList.remove('is-dragging');
      const dropOn = target;
      setTarget(null);
      if (dragging && dropOn) this.swapCells(cell, dropOn);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', stop);
    document.addEventListener('pointercancel', stop);
  }

  // ── Drag (ported from blitzen2d workspace/gutters.js) ──

  #startDrag(e: PointerEvent, g: HTMLElement, edge: BentoEdge): void {
    if (e.button !== 0) return;
    e.preventDefault();
    const axis = edge.axis;
    const startCursor = axis === 'col' ? e.clientX : e.clientY;
    // Baseline AT DRAG START: every preview slides from this snapshot so
    // edge ids stay valid and snap targets don't drift mid-gesture.
    const baseline = this.#cells.map((c) => ({ ...c }));
    const container = this.#size();
    const snapRadius = parseFloat(this.getAttribute('snap') ?? '4');
    const minPx = parseFloat(this.getAttribute('min') ?? '50');

    const snapTargets: number[] = [edge.coord, 0, axis === 'col' ? container.w : container.h];
    for (const other of bentoEdges(baseline, container)) {
      if (other.id !== edge.id && other.axis === axis) snapTargets.push(other.coord);
    }

    this.setAttribute('data-ry-resizing', '');
    g.classList.add('is-dragging');
    document.body.style.cursor = axis === 'col' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';

    let lastResult: SlideResult | null = null;
    let lastDelta = 0;
    let pendingFrame = 0;

    const move = (ev: PointerEvent) => {
      const cursorPx = edge.coord + ((axis === 'col' ? ev.clientX : ev.clientY) - startCursor);
      const target = bentoSnap(snapTargets, cursorPx, snapRadius);
      const result = bentoSlide(baseline, edge.id, target - edge.coord, container, minPx);
      lastResult = result;
      lastDelta = result.ok ? result.actualDelta : 0;
      // The strip follows the APPLIED delta, never the raw cursor — so it
      // can't slip past cells that stopped at their min.
      g.style.transform = axis === 'col' ? `translateX(${lastDelta}px)` : `translateY(${lastDelta}px)`;
      if (!pendingFrame) {
        pendingFrame = requestAnimationFrame(() => {
          pendingFrame = 0;
          if (lastResult?.ok) {
            this.#paint(lastResult.cells);
            this.emit('resize', { layout: lastResult.cells.map(({ x, y, w, h }) => ({ x, y, w, h })) });
          }
        });
      }
    };
    const stop = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', stop);
      document.removeEventListener('pointercancel', stop);
      if (pendingFrame) { cancelAnimationFrame(pendingFrame); pendingFrame = 0; }
      this.removeAttribute('data-ry-resizing');
      g.classList.remove('is-dragging');
      g.style.transform = '';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (lastDelta !== 0 && lastResult?.ok) {
        this.#commit(lastResult.cells);
      } else {
        this.#paint(baseline); // wipe any partial preview from a jitter
      }
      this.emit('resize', { layout: this.layout });
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', stop);
    document.addEventListener('pointercancel', stop);
  }

  #onKey(e: KeyboardEvent, edge: BentoEdge): void {
    const grow = edge.axis === 'col' ? 'ArrowRight' : 'ArrowDown';
    const shrink = edge.axis === 'col' ? 'ArrowLeft' : 'ArrowUp';
    if (e.key !== grow && e.key !== shrink) return;
    e.preventDefault();
    const step = (e.shiftKey ? 50 : 10) * (e.key === grow ? 1 : -1);
    const minPx = parseFloat(this.getAttribute('min') ?? '50');
    const result = bentoSlide(this.#cells, edge.id, step, this.#size(), minPx);
    if (!result.ok || result.actualDelta === 0) return;
    this.#commit(result.cells);
    this.emit('resize', { layout: this.layout });
    // #commit remounted the gutters — put focus back on this edge.
    this.querySelector<HTMLElement>(`[data-edge-id="${edge.id}"]`)?.focus();
  }

  // ── Persistence (percent rects — responsive, unlike px) ──

  #key(): string | null {
    const k = this.getAttribute('persist');
    return k ? `ry-bento:${k}` : null;
  }

  #persist(): void {
    const k = this.#key();
    if (!k) return;
    const rects = this.#cells.map((c) => [c.x, c.y, c.w, c.h].map((n) => Math.round(n * 100) / 100));
    try { localStorage.setItem(k, JSON.stringify(rects)); } catch { /* */ }
  }

  #restore(): void {
    const k = this.#key();
    if (!k) return;
    try {
      const saved = localStorage.getItem(k);
      if (!saved) return;
      const arr = JSON.parse(saved) as number[][];
      if (!Array.isArray(arr) || arr.length !== this.#cells.length) return;
      const cells = this.#cells.map((c, i) => ({
        id: c.id, x: arr[i]![0]!, y: arr[i]![1]!, w: arr[i]![2]!, h: arr[i]![3]!,
      }));
      if (bentoValidate(cells)) return; // corrupt save — keep declarative layout
      this.#cells = cells;
    } catch { /* */ }
  }
}

customElements.define('ry-bento', RyBento);

declare global {
  interface HTMLElementTagNameMap {
    'ry-bento': RyBento;
  }
}
